import { Loader2 } from 'lucide-react';
import type { CSSProperties } from 'react';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ServerStatus } from '@/types/fleet';

type BadgeVariant = 'active' | 'degraded' | 'failed' | 'inflight' | 'idle';

const STATUS_STYLES: Record<
  ServerStatus,
  { label: string; variant: BadgeVariant; dot: string }
> = {
  active: { label: 'Active', variant: 'active', dot: 'var(--status-active)' },
  provisioning: { label: 'Provisioning', variant: 'inflight', dot: 'var(--status-inflight)' },
  quarantined: { label: 'Quarantined', variant: 'degraded', dot: 'var(--status-degraded)' },
  decommissioned: { label: 'Decommissioned', variant: 'idle', dot: 'var(--status-idle)' },
};

const FALLBACK = { label: 'Unknown', variant: 'idle' as const, dot: 'var(--status-idle)' };

export function StatusBadge({
  status,
  className,
  /**
   * When set, a "pending" overlay (spinner + caption) is rendered alongside
   * the badge. Used by the action flow: the canonical `status` is NOT flipped
   * optimistically — the overlay signals an in-flight task instead.
   */
  pending,
}: {
  status: ServerStatus;
  className?: string;
  pending?: { label: string };
}): JSX.Element {
  const style = STATUS_STYLES[status] ?? { ...FALLBACK, label: status };

  const badge = (
    <Badge variant={style.variant} className={cn('gap-1.5', className)}>
      <span
        className="signal-dot"
        style={{ ['--bg-dot']: style.dot, backgroundColor: style.dot } as CSSProperties}
        aria-hidden
      />
      {style.label}
    </Badge>
  );

  if (!pending) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1.5">
          {badge}
          <span className="inline-flex items-center gap-1 rounded border border-status-inflight/30 bg-status-inflight/10 px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wide text-status-inflight">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            pending
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{pending.label}</TooltipContent>
    </Tooltip>
  );
}
